import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { useSettings } from "@/context/SettingsContext";

// The ॐ mandala visual — concentric rotating rings + a glowing, breathing Om.
export function OmVisual({ size = 160 }) {
  return (
    <div className="relative grid place-items-center" style={{ width: size, height: size }} data-testid="om-visual">
      {/* radial glow */}
      <div
        className="om-glow absolute inset-0 rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(199,80,0,0.30), rgba(198,138,0,0.12) 45%, transparent 70%)",
          filter: "blur(8px)",
        }}
      />
      {/* outer dotted ring */}
      <svg className="om-spin absolute" width={size} height={size} viewBox="0 0 100 100" fill="none">
        <circle cx="50" cy="50" r="47" stroke="var(--gold-muted)" strokeWidth="0.6" strokeDasharray="1.5 4" opacity="0.75" />
      </svg>
      {/* middle dashed ring (reverse) */}
      <svg className="om-spin-rev absolute" width={size * 0.8} height={size * 0.8} viewBox="0 0 100 100" fill="none">
        <circle cx="50" cy="50" r="46" stroke="var(--saffron)" strokeWidth="0.9" strokeDasharray="16 9" opacity="0.5" />
      </svg>
      {/* inner thin ring */}
      <svg className="om-spin absolute" width={size * 0.62} height={size * 0.62} viewBox="0 0 100 100" fill="none">
        <circle cx="50" cy="50" r="46" stroke="var(--gold)" strokeWidth="0.6" opacity="0.4" />
      </svg>
      {/* the Om */}
      <span
        className="om-pulse relative font-deva"
        style={{
          fontSize: size * 0.4,
          lineHeight: 1,
          padding: "0.1em",
          display: "inline-block",
          overflow: "visible",
          transform: "translateY(0.04em)",
          background: "linear-gradient(135deg, var(--saffron), var(--gold))",
          WebkitBackgroundClip: "text",
          backgroundClip: "text",
          color: "transparent",
          filter: "drop-shadow(0 3px 16px rgba(199,80,0,0.38))",
        }}
      >
        ॐ
      </span>
    </div>
  );
}

// Full-screen branded loader shown once while the app boots.
export function BootLoader() {
  const { loaded } = useSettings();
  const [hide, setHide] = useState(false);

  useEffect(() => {
    let t;
    if (loaded) t = setTimeout(() => setHide(true), 1100); // graceful minimum
    const hard = setTimeout(() => setHide(true), 4000); // safety net
    return () => {
      if (t) clearTimeout(t);
      clearTimeout(hard);
    };
  }, [loaded]);

  return (
    <AnimatePresence>
      {!hide && (
        <motion.div
          key="om-boot"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.7, ease: "easeInOut" }}
          className="fixed inset-0 z-[100] grid place-items-center grain"
          style={{ backgroundColor: "var(--parchment)" }}
          data-testid="om-boot-loader"
        >
          <div className="flex flex-col items-center gap-7">
            <OmVisual size={172} />
            <div className="flex flex-col items-center gap-1.5">
              <span className="font-deva text-3xl text-ink">धर्मार्थ</span>
              <span className="overline-label text-ink-3" style={{ letterSpacing: "0.34em" }}>DHARMARTHA</span>
            </div>
            <div className="mt-1 h-[2px] w-44 overflow-hidden" style={{ backgroundColor: "var(--line)" }}>
              <div className="om-loadbar h-full w-1/3" style={{ background: "linear-gradient(90deg, transparent, var(--saffron), transparent)" }} />
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// A quick, elegant ॐ veil that sweeps in on every route change.
export function RouteOmTransition() {
  const { pathname } = useLocation();
  const [show, setShow] = useState(false);
  const first = useRef(true);

  useEffect(() => {
    if (first.current) {
      first.current = false;
      return; // don't run over the boot loader
    }
    setShow(true);
    const t = setTimeout(() => setShow(false), 560);
    return () => clearTimeout(t);
  }, [pathname]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="om-veil"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.26, ease: "easeInOut" }}
          className="pointer-events-none fixed inset-0 z-[90] grid place-items-center"
          data-testid="om-route-transition"
        >
          <div className="absolute inset-0" style={{ backgroundColor: "var(--parchment)", opacity: 0.84, backdropFilter: "blur(2px)" }} />
          <motion.div
            className="relative"
            initial={{ scale: 0.62, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 1.18, opacity: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          >
            <OmVisual size={124} />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
